import { Link } from 'react-router-dom'
import { useEffect } from 'react'
import { useSEO } from '@/hooks/useSEO'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Search, FileQuestion } from 'lucide-react'

/**
 * 404 page rendered when no other route matches.
 *
 * SPA limitation: this page is served from index.html with HTTP 200 from
 * nginx's try_files fallback. We can't change the status code from client
 * JS, but we add <meta name="robots" content="noindex"> via useSEO so
 * Googlebot won't index the URL, and we add a "prerender-status-code"
 * meta as a hint for some prerender services. The prerender script
 * (scripts/prerender.mjs) also outputs this page to dist/404.html which
 * nginx serves with a real HTTP 404 for explicit error_page lookups
 * (when configured to do so).
 */
export default function NotFound() {
  useSEO({
    title: 'Page not found',
    description: 'The page you were looking for does not exist.',
  })

  useEffect(() => {
    // Tell crawlers not to index this URL even though the server returns 200
    const setMeta = (name: string, value: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null
      if (!el) {
        el = document.createElement('meta')
        el.setAttribute('name', name)
        document.head.appendChild(el)
      }
      el.setAttribute('content', value)
    }
    setMeta('robots', 'noindex, nofollow')
    setMeta('prerender-status-code', '404')
    return () => {
      // Restore default robots on unmount so navigating away doesn't carry it
      const robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null
      if (robots) robots.setAttribute('content', 'index, follow')
      const status = document.querySelector('meta[name="prerender-status-code"]')
      if (status) status.remove()
    }
  }, [])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-16">
      <div className="max-w-lg w-full text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-100 text-blue-600 mb-6">
          <FileQuestion className="h-8 w-8" />
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-3">
          Page not found
        </h1>
        <p className="text-gray-600 mb-8">
          We couldn't find what you were looking for. The link may be broken or the page may have moved.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild>
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to home
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/blog">
              <Search className="h-4 w-4 mr-2" />
              Browse the blog
            </Link>
          </Button>
        </div>
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          <Link to="/solutions" className="p-3 rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-colors">
            Solutions
          </Link>
          <Link to="/case-studies" className="p-3 rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-colors">
            Case Studies
          </Link>
          <Link to="/blog" className="p-3 rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-colors">
            Blog
          </Link>
          <Link to="/extract" className="p-3 rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-colors">
            Document Types
          </Link>
          <Link to="/compare" className="p-3 rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-colors">
            Comparisons
          </Link>
          <Link to="/glossary" className="p-3 rounded-lg border border-gray-200 hover:border-blue-200 hover:bg-blue-50 transition-colors">
            Glossary
          </Link>
        </div>
      </div>
    </div>
  )
}
